import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { getExternalThumbnail } from '@/utils/media'

interface Video {
  id: string
  title?: string
  duration?: number
  orderIndex?: number
  videoUrl?: string
}

interface VideoCardProps {
  video: Video
  moduleId: string
  moduleTitle: string
  moduleThumbnail?: string | undefined
  watchedPercentage?: number | undefined
  isCompleted?: boolean | undefined
}

export default function VideoCard({ video, moduleId, moduleTitle, moduleThumbnail, watchedPercentage, isCompleted }: VideoCardProps) {
  const navigate = useNavigate()
  const [extractedThumb, setExtractedThumb] = useState<string | null>(null)
  const [extractionFailed, setExtractionFailed] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (!video.videoUrl) {
      setExtractionFailed(true)
      return
    }

    // Skip extraction for non-direct video URLs (e.g. YouTube)
    const url = video.videoUrl.toLowerCase()
    if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com')) {
      setExtractionFailed(true)
      return
    }

    const vid = document.createElement('video')
    vid.crossOrigin = 'anonymous'
    vid.preload = 'metadata'
    vid.muted = true
    videoRef.current = vid

    vid.onloadeddata = () => {
      // Seek to 1 second to get a meaningful frame
      vid.currentTime = Math.min(1, vid.duration * 0.1)
    }

    vid.onseeked = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = vid.videoWidth
        canvas.height = vid.videoHeight
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(vid, 0, 0, canvas.width, canvas.height)
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
          setExtractedThumb(dataUrl)
        } else {
          setExtractionFailed(true)
        }
      } catch {
        setExtractionFailed(true)
      }
    }

    vid.onerror = () => {
      setExtractionFailed(true)
    }

    vid.src = video.videoUrl

    return () => {
      vid.onloadeddata = null
      vid.onseeked = null
      vid.onerror = null
      vid.src = ''
      videoRef.current = null
    }
  }, [video.videoUrl])

  const externalThumb = video.videoUrl ? getExternalThumbnail(video.videoUrl) : null
  const thumbnailSrc = extractedThumb || externalThumb || (extractionFailed ? moduleThumbnail : null)

  const formatDuration = (seconds?: number) => {
    if (!seconds && seconds !== 0) return '—'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const handleClick = () => {
    navigate(`/learning-path/video/${video.id}`, {
      state: { video, moduleTitle, moduleId, moduleThumbnail },
    })
  }

  return (
    <div
      className='group cursor-pointer'
      onClick={handleClick}
    >
      {/* Thumbnail Container */}
      <div className='relative aspect-video rounded-[12px] overflow-hidden mb-3'>
        {thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt={video.title || moduleTitle}
            className='absolute inset-0 h-full w-full object-cover'
          />
        ) : (
          <div className='absolute inset-0 bg-gradient-to-br from-[#FFB366] via-[#FF9933] to-[#FF8000]' />
        )}

        {/* Completed Overlay */}
        {isCompleted ? (
          <div className='absolute inset-0 bg-black/40 flex items-center justify-center z-10'>
            <div className='w-12 h-12 rounded-full bg-green-500 flex items-center justify-center'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                <path d='M16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z' fill='white' />
              </svg>
            </div>
          </div>
        ) : (
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='h-[48px] w-[48px] rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform'>
              <svg
                className='h-5 w-5 text-[#151515] ml-0.5'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path d='M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z' />
              </svg>
            </div>
          </div>
        )}

        {/* Duration Badge - Bottom Right */}
        <div className='absolute bottom-2 right-2 px-2 py-0.5 bg-[#151515]/80 text-white text-[12px] font-medium rounded-[4px] z-10'>
          {formatDuration(video.duration)}
        </div>

        {/* Watch Progress Bar */}
        {!isCompleted && watchedPercentage != null && watchedPercentage > 0 && (
          <div className='absolute bottom-0 left-0 right-0 h-1 bg-black/20 z-10'>
            <div
              className='h-full bg-[#FA6017] transition-all duration-300'
              style={{ width: `${Math.min(watchedPercentage, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className='flex flex-col gap-1'>
        {/* Video Badge */}
        <div className='flex items-center'>
          <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-[4px] gap-1 ${
            isCompleted ? 'bg-green-600 text-white' : 'bg-[#151515] text-white'
          }`}>
            {isCompleted ? (
              <>
                <svg width='12' height='12' viewBox='0 0 24 24' fill='none'>
                  <path d='M16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z' fill='white' />
                </svg>
                Completed
              </>
            ) : (
              <>
                <svg className='h-3 w-3' fill='currentColor' viewBox='0 0 20 20'>
                  <path d='M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z' />
                </svg>
                Video {video.orderIndex || 1}
              </>
            )}
          </span>
        </div>
        {/* Video Title */}
        <h4 className={`text-[14px] font-medium leading-[20px] line-clamp-2 transition-colors ${
          isCompleted ? 'text-green-700' : 'text-[#151515] group-hover:text-[#FA6017]'
        }`}>
          {video.title}
        </h4>
        {/* Watch percentage */}
        {!isCompleted && watchedPercentage != null && watchedPercentage > 0 && (
          <span className='text-[12px] font-medium text-[#747474]'>
            {Math.round(watchedPercentage)}% watched
          </span>
        )}
      </div>
    </div>
  )
}
